import {
  type RefCallback,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type OverflowAxis = "both" | "horizontal" | "vertical";

export type OverflowObserverOptions = {
  axis?: OverflowAxis;
  includeDescendants?: boolean;
};

/**
 * Observe overflow on an element (optionally including descendants) and return a ref and boolean.
 * Uses ResizeObserver + MutationObserver to react to size/content changes.
 */
export const useOverflowObserver = <
  TElement extends HTMLElement = HTMLDivElement,
>(
  options?: OverflowObserverOptions,
): [RefCallback<TElement>, boolean] => {
  const { axis = "both", includeDescendants = true } = options ?? {};

  const refObject = useRef<TElement | null>(null);
  const [observedNode, setObservedNode] = useState<TElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const setRef: RefCallback<TElement> = useCallback((node) => {
    refObject.current = node;
    setObservedNode(node);
  }, []);

  useLayoutEffect(() => {
    const element = observedNode as HTMLElement | null;

    if (!element) {
      return;
    }

    const getElementsToCheck = (): HTMLElement[] => {
      if (!includeDescendants) {
        return [element];
      }

      return [
        element,
        ...Array.from(element.querySelectorAll<HTMLElement>("*")),
      ];
    };

    const checkOverflow = () => {
      const elementsToCheck = getElementsToCheck();

      const hasOverflow = elementsToCheck.some((el) => {
        const hasHorizontal = el.scrollWidth > el.clientWidth;
        const hasVertical = el.scrollHeight > el.clientHeight;

        if (axis === "horizontal") return hasHorizontal;

        if (axis === "vertical") return hasVertical;

        return hasHorizontal || hasVertical;
      });

      setIsOverflowing(hasOverflow);
    };

    // Observe size changes of the container
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);

    // Observe subtree/content changes that might affect overflow without resizing the container
    const mutationObserver = new MutationObserver(checkOverflow);
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    // Some assets (e.g., images) load later and can affect overflow. Capture load events.
    element.addEventListener("load", checkOverflow, true);
    element.addEventListener("transitionend", checkOverflow, true);

    // Initial check
    checkOverflow();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      element.removeEventListener("load", checkOverflow, true);
      element.removeEventListener("transitionend", checkOverflow, true);
    };
  }, [axis, includeDescendants, observedNode]);

  return [setRef, isOverflowing] as const;
};
